import { FC, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { PayloadSchemaField } from '@/services/data-sources/get-collection-info';

export type FieldOverrideValue =
  | string
  | number
  | boolean
  | string[]
  | null
  | undefined;

export type FieldOverrides = Record<string, FieldOverrideValue>;

interface FieldOverridesFormProps {
  exposedAttributes: string[];
  payloadSchema?: Record<string, PayloadSchemaField>;
  values: FieldOverrides;
  onChange: (next: FieldOverrides) => void;
  disabled?: boolean;
  arrayFields?: Set<string>;
}

const isTextualType = (dataType: string): boolean => {
  return dataType === 'keyword' || dataType === 'text';
};

export const buildFieldOverridesPayload = (
  exposedAttributes: string[],
  values: FieldOverrides,
  payloadSchema?: Record<string, PayloadSchemaField>,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  exposedAttributes.forEach((field) => {
    const raw = values[field];
    const schema = payloadSchema?.[field];

    if (raw === undefined || raw === null || raw === '') return;

    if (Array.isArray(raw)) {
      const cleaned = raw
        .map((v) => (typeof v === 'string' ? v.trim() : v))
        .filter((v) => v !== '' && v !== null && v !== undefined);
      if (cleaned.length === 0) return;
      payload[field] = cleaned;
      return;
    }

    if (schema?.data_type === 'integer') {
      const num = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(num)) payload[field] = Math.trunc(num);
      return;
    }

    if (schema?.data_type === 'float') {
      const num = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(num)) payload[field] = num;
      return;
    }

    if (schema?.data_type === 'bool') {
      payload[field] = Boolean(raw);
      return;
    }

    if (schema?.data_type === 'datetime') {
      if (typeof raw === 'string' && raw.trim()) {
        const date = new Date(raw);
        if (!Number.isNaN(date.getTime())) {
          payload[field] = date.toISOString();
        }
      }
      return;
    }

    payload[field] = raw;
  });

  return payload;
};

interface MultiStringInputProps {
  id: string;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MultiStringInput: FC<MultiStringInputProps> = ({
  id,
  values,
  onChange,
  disabled,
  placeholder,
}) => {
  const rows = values.length > 0 ? values : [''];

  const updateRow = (index: number, next: string) => {
    const copy = [...rows];
    copy[index] = next;
    onChange(copy);
  };

  const removeRow = (index: number) => {
    const copy = rows.filter((_, i) => i !== index);
    onChange(copy.length === 0 ? [] : copy);
  };

  const addRow = () => {
    onChange([...rows, '']);
  };

  return (
    <div className='space-y-2'>
      {rows.map((val, idx) => (
        <div key={`${id}-${idx}`} className='flex items-center gap-2'>
          <Input
            id={idx === 0 ? id : undefined}
            value={val}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => updateRow(idx, e.target.value)}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            disabled={disabled || rows.length === 1}
            onClick={() => removeRow(idx)}
            aria-label={`Remove value ${idx + 1}`}
          >
            <X className='size-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={disabled}
        onClick={addRow}
      >
        <Plus className='size-4 mr-1' />
        Add another
      </Button>
    </div>
  );
};

export const FieldOverridesForm: FC<FieldOverridesFormProps> = ({
  exposedAttributes,
  payloadSchema,
  values,
  onChange,
  disabled,
  arrayFields,
}) => {
  const sortedFields = useMemo(
    () => [...exposedAttributes].sort(),
    [exposedAttributes],
  );

  if (sortedFields.length === 0) return null;

  const setValue = (field: string, value: FieldOverrideValue) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <div className='space-y-4'>
      {sortedFields.map((field) => {
        const schema = payloadSchema?.[field];
        const dataType = schema?.data_type || 'keyword';
        const id = `field-override-${field}`;
        const current = values[field];

        return (
          <div key={field} className='space-y-1.5'>
            <div className='flex items-center gap-2'>
              <Label htmlFor={id} className='text-sm font-medium'>
                {field}
              </Label>
              <Badge variant='outline' className='font-mono text-xs'>
                {dataType}
              </Badge>
            </div>

            {isTextualType(dataType) && arrayFields?.has(field) && (
              <MultiStringInput
                id={id}
                values={Array.isArray(current) ? current : []}
                onChange={(next) => setValue(field, next)}
                disabled={disabled}
                placeholder={`Enter ${field} value`}
              />
            )}

            {isTextualType(dataType) && !arrayFields?.has(field) && (
              <Input
                id={id}
                value={typeof current === 'string' ? current : ''}
                disabled={disabled}
                placeholder={`Enter ${field} value`}
                onChange={(e) => setValue(field, e.target.value)}
              />
            )}

            {dataType === 'bool' && (
              <div className='flex items-center gap-2'>
                <Switch
                  id={id}
                  checked={Boolean(current)}
                  disabled={disabled}
                  onCheckedChange={(checked) => setValue(field, checked)}
                />
                <span className='text-xs text-muted-foreground'>
                  {current ? 'True' : 'False'}
                </span>
              </div>
            )}

            {(dataType === 'integer' || dataType === 'float') && (
              <Input
                id={id}
                type='number'
                step={dataType === 'integer' ? 1 : 'any'}
                value={
                  typeof current === 'number' || typeof current === 'string'
                    ? current
                    : ''
                }
                disabled={disabled}
                onChange={(e) => setValue(field, e.target.value)}
              />
            )}

            {dataType === 'datetime' && (
              <Input
                id={id}
                type='datetime-local'
                value={typeof current === 'string' ? current : ''}
                disabled={disabled}
                onChange={(e) => setValue(field, e.target.value)}
              />
            )}

            {dataType === 'uuid' && (
              <Input
                id={id}
                value={typeof current === 'string' ? current : ''}
                disabled={disabled}
                onChange={(e) => setValue(field, e.target.value)}
                placeholder='00000000-0000-0000-0000-000000000000'
              />
            )}

            {dataType === 'geo' && (
              <Input
                id={id}
                value={typeof current === 'string' ? current : ''}
                disabled={disabled}
                onChange={(e) => setValue(field, e.target.value)}
                placeholder='lat,lon'
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
